import { useState, useRef, useEffect, useCallback } from "react";
import { useForm } from "../../context/FormContext";
import { useLang } from "../../context/LanguageContext";
import { canvasHasSignatureInk } from "../../utils/signatureFieldValidation";

/* ─── Checkbox ─── */
interface CheckboxRowProps {
  fieldId?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  children: React.ReactNode;
  t: (key: string) => string;
}

function CheckboxRow({ fieldId, checked, onChange, children }: CheckboxRowProps) {
  return (
    <label
      id={fieldId}
      className="flex items-start gap-3 cursor-pointer group select-none py-3 border-b border-slate-50 last:border-0"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onChange(!checked); } }}
    >
      <div className="mt-0.5 shrink-0">
        <div
          onClick={() => onChange(!checked)}
          className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
            checked
              ? "bg-slate-800 border-slate-800"
              : "border-slate-300 bg-white group-hover:border-slate-400"
          }`}
        >
          {checked && <i className="ri-check-line text-white text-xs" />}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[14px] text-slate-700 leading-snug">{children}</p>
      </div>
    </label>
  );
}

/* ─── Signature Pad ─── */
interface SignaturePadProps {
  hasSigned: boolean;
  onSign: (signed: boolean) => void;
  onSignatureData: (data: string) => void;
  onRejectedBlankSave?: () => void;
  onClearBlankWarning?: () => void;
  initialSignature?: string;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  signatureLabel: string;
  clearLabel: string;
  hintText: string;
  bottomLabel: string;
}

function SignaturePad({
  hasSigned,
  onSign,
  onSignatureData,
  onRejectedBlankSave,
  onClearBlankWarning,
  initialSignature,
  canvasRef,
  signatureLabel,
  clearLabel,
  hintText,
  bottomLabel,
}: SignaturePadProps) {
  const isDrawingRef = useRef(false);
  const lastPos = useRef<{ x: number; y: number } | null>(null);
  const onSignRef = useRef(onSign);
  const onSignatureDataRef = useRef(onSignatureData);
  const onClearBlankWarningRef = useRef(onClearBlankWarning);
  onSignRef.current = onSign;
  onSignatureDataRef.current = onSignatureData;
  onClearBlankWarningRef.current = onClearBlankWarning;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = window.devicePixelRatio || 1;

    const setup = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.scale(dpr, dpr);
        ctx.strokeStyle = "#1e293b";
        ctx.lineWidth = 1.8;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
      }
    };

    setup();

    if (initialSignature) {
      const img = new Image();
      img.onload = () => {
        const ctx = canvas.getContext("2d");
        const scale = window.devicePixelRatio || 1;
        if (ctx) {
          ctx.drawImage(img, 0, 0, canvas.width / scale, canvas.height / scale);
          if (canvasHasSignatureInk(canvas)) {
            onSignRef.current(true);
            onClearBlankWarningRef.current?.();
          } else {
            onSignRef.current(false);
            onSignatureDataRef.current("");
          }
        }
      };
      img.src = initialSignature;
    }

    window.addEventListener("resize", setup);
    return () => window.removeEventListener("resize", setup);
  }, [canvasRef, initialSignature]);

  const getXY = (e: React.MouseEvent | React.Touch): { x: number; y: number } => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const startDraw = useCallback((pos: { x: number; y: number }) => {
    isDrawingRef.current = true;
    lastPos.current = pos;
    const ctx = canvasRef.current?.getContext("2d");
    if (ctx) { ctx.beginPath(); ctx.moveTo(pos.x, pos.y); }
  }, [canvasRef]);

  const continueDraw = useCallback((pos: { x: number; y: number }) => {
    if (!isDrawingRef.current) return;
    const ctx = canvasRef.current?.getContext("2d");
    if (ctx) {
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    }
    lastPos.current = pos;
  }, [canvasRef]);

  const endDraw = useCallback(
    (shouldSave = false) => {
      isDrawingRef.current = false;
      lastPos.current = null;
      if (!shouldSave) return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      if (!canvasHasSignatureInk(canvas)) {
        onRejectedBlankSave?.();
        onSign(false);
        return;
      }
      onSignatureData(canvas.toDataURL("image/png"));
      onSign(true);
      onClearBlankWarning?.();
    },
    [canvasRef, onSignatureData, onSign, onRejectedBlankSave, onClearBlankWarning]
  );

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    onSign(false);
    onSignatureData("");
    onClearBlankWarning?.();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[12px] font-semibold text-slate-500 uppercase tracking-wide">
          {signatureLabel}
        </p>
        {hasSigned && (
          <button
            type="button"
            onClick={clear}
            className="text-[12px] text-slate-400 hover:text-slate-600 transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap"
          >
            <i className="ri-delete-bin-line text-xs" />
            {clearLabel}
          </button>
        )}
      </div>

      <div
        id="field-signature"
        className={`relative rounded-xl border-2 transition-colors overflow-hidden ${
          hasSigned ? "border-slate-300" : "border-dashed border-slate-200"
        }`}
        style={{ height: "120px" }}
      >
        {!hasSigned && (
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none gap-1">
            <i className="ri-edit-2-line text-slate-300 text-xl" />
            <p className="text-[12px] text-slate-300 font-medium">{hintText}</p>
          </div>
        )}
        <canvas
          ref={canvasRef as React.RefObject<HTMLCanvasElement>}
          role="img"
          aria-label={signatureLabel}
          className="w-full h-full cursor-crosshair touch-none"
          onMouseDown={(e) => startDraw(getXY(e))}
          onMouseMove={(e) => continueDraw(getXY(e))}
          onMouseUp={() => endDraw(true)}
          onMouseLeave={() => endDraw(false)}
          onTouchStart={(e) => { e.preventDefault(); startDraw(getXY(e.touches[0])); }}
          onTouchMove={(e) => { e.preventDefault(); continueDraw(getXY(e.touches[0])); }}
          onTouchEnd={() => endDraw(true)}
        />
      </div>

      <div className="mt-1 flex items-center gap-2">
        <div className="flex-1 border-b border-slate-200" />
        <p className="text-[11px] text-slate-400 whitespace-nowrap">{bottomLabel}</p>
        <div className="flex-1 border-b border-slate-200" />
      </div>
    </div>
  );
}

/* ─── Scope item ─── */
function ScopeItem({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-2.5 text-[13px] text-slate-600">
      <span className="text-slate-400 mt-0.5 shrink-0">•</span>
      <p className="leading-relaxed">{text}</p>
    </div>
  );
}

type EngagementVariant = "default" | "procedure";

function engagementKey(variant: EngagementVariant, key: string): string {
  return variant === "procedure" ? `engagementProcedure.${key}` : `engagement.${key}`;
}

/* ─── Main section ─── */
export default function EngagementSection({
  variant = "default",
}: {
  /** Procedure/org flows use generic copy (no Spain regularization text). */
  variant?: EngagementVariant;
}) {
  const { data, update } = useForm();
  const { t, isRTL, lang } = useLang();
  const tk = (key: string) => t(engagementKey(variant, key));

  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const [hasSigned, setHasSigned] = useState(!!data.signature);
  const [blankSigHint, setBlankSigHint] = useState(false);

  const handleRejectedBlankSave = useCallback(() => setBlankSigHint(true), []);
  const handleClearBlankHint = useCallback(() => setBlankSigHint(false), []);

  useEffect(() => {
    if (data.marketingConsent) {
      update("marketingConsent", false);
    }
  }, [data.marketingConsent, update]);

  const advisorName = data.advisor || "Global Mobility";
  const advisorInitials = advisorName
    .trim()
    .split(/\s+/)
    .map((p) => p.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const today = new Date().toLocaleDateString(
    lang === "zh" ? "zh-CN" : lang === "ar" ? "ar-SA" : lang === "fr" ? "fr-FR" : lang === "en" ? "en-GB" : "es-ES",
    { day: "numeric", month: "long", year: "numeric" }
  );

  const allMandatoryChecked = data.engagementConsent && data.dataProcessingConsent;
  const formComplete = allMandatoryChecked && hasSigned;

  return (
    <div className="max-w-[480px] mx-auto px-4 pt-6 pb-8" dir={isRTL ? "rtl" : "ltr"}>
      {/* Section header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-9 h-9 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center shrink-0">
            <i className="ri-file-text-line text-slate-500 text-lg" />
          </div>
          <div>
            <h1 className="text-xl font-display text-slate-900 leading-tight">{tk("title")}</h1>
            <p className="text-[12px] text-slate-500 mt-0.5">{tk("subtitle")}</p>
          </div>
        </div>
      </div>

      {/* ── Why callout ── */}
      <div className="bg-emerald-50 border border-emerald-100 rounded-2xl px-4 py-4 mb-3 flex items-start gap-3">
        <div className="w-7 h-7 flex items-center justify-center shrink-0 mt-0.5">
          <i className="ri-information-line text-emerald-600 text-base" />
        </div>
        <div>
          <p className="text-[13px] font-semibold text-emerald-800 mb-0.5">
            {tk("whyTitle")}
          </p>
          <p className="text-[13px] text-emerald-700 leading-relaxed">
            {tk("whyBody")}
          </p>
        </div>
      </div>

      {/* ── Advisor profile card ── */}
      <div className="bg-white rounded-2xl border border-slate-100 px-4 py-4 mb-4">
        {/* Profile row */}
        <div className="flex items-center gap-3 mb-3 pb-3 border-b border-slate-50">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center text-white text-[13px] font-bold shrink-0"
            style={{ background: "#059669" }}
          >
            {advisorInitials}
          </div>
          <div className="min-w-0">
            <p className="text-[14px] font-semibold text-slate-900">{advisorName}</p>
            <p className="text-[12px] text-slate-500">{tk("advisorRole")}</p>
          </div>
          <div className="ml-auto shrink-0">
            <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2.5 py-1 whitespace-nowrap">
              {tk("assignedBadge")}
            </span>
          </div>
        </div>

        {/* Scope */}
        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2.5">
          {tk("scopeLabel")}
        </p>
        <div className="flex flex-col gap-2">
          <ScopeItem text={tk("scope.analysis")} />
          <ScopeItem text={tk("scope.preparation")} />
          <ScopeItem text={tk("scope.submission")} />
          <ScopeItem text={tk("scope.followup")} />
        </div>
      </div>

      {/* ── Fee confirmation (Spain regularization only) ── */}
      {variant === "default" ? (
        <div className="bg-white rounded-2xl border border-slate-100 px-4 py-4 mb-4">
          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-3">
            {tk("feeLabel")}
          </p>
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-[14px] font-medium text-slate-800">
                {tk("feeTitle")}
              </p>
              <p className="text-[12px] text-slate-500 mt-0.5">
                {tk("feeNote")}
              </p>
            </div>
            <div className="text-right shrink-0 ml-4">
              <p className="text-[22px] font-bold text-slate-900 leading-none">{tk("feeAmount")}</p>
              <p className="text-[11px] text-slate-500 mt-0.5">{tk("feeWord")}</p>
            </div>
          </div>
          <div className="bg-slate-50 rounded-xl px-3 py-2.5 text-[12px] text-slate-500 flex items-center gap-2">
            <i className="ri-calendar-check-line text-slate-500 text-sm shrink-0" />
            <span>{tk("feeAmountNote")}</span>
          </div>
        </div>
      ) : null}

      {/* ── Consent checkboxes ── */}
      <div className="bg-white rounded-2xl border border-slate-100 px-4 mb-4">
        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide pt-4 pb-2 mb-0">
          {tk("consentsLabel")}
        </p>
        <CheckboxRow
          fieldId="field-engagementConsent"
          checked={data.engagementConsent}
          onChange={(v) => update("engagementConsent", v)}
          t={t}
        >
          {tk("consent1Before")}
          <a
            href="https://globalmobility.law/condiciones-generales"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="underline decoration-dotted text-slate-800 hover:text-slate-600 transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            {tk("consent1Link")}
          </a>
          {variant === "default" ? tk("consent1After") : tk("consent1AfterGeneric")}
        </CheckboxRow>
        <CheckboxRow
          fieldId="field-dataProcessingConsent"
          checked={data.dataProcessingConsent}
          onChange={(v) => update("dataProcessingConsent", v)}
          t={t}
        >
          {tk("consent2")}
        </CheckboxRow>
      </div>

      {/* ── Questions follow-up ── */}
      <div className="bg-slate-50 rounded-2xl border border-slate-100 px-4 py-4 mb-4 text-center">
        <p className="text-[13px] text-slate-600 mb-3">{tk("questionsText")}</p>
        <button
          type="button"
          onClick={() => update("questionsRequested", true)}
          disabled={data.questionsRequested}
          className={`w-full min-h-[40px] flex items-center justify-center gap-2 rounded-xl text-[13px] font-semibold transition-colors ${
            data.questionsRequested
              ? "bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-default"
              : "bg-white border border-slate-200 hover:border-slate-300 text-slate-700 cursor-pointer"
          }`}
        >
          {data.questionsRequested ? (
            <>
              <i className="ri-checkbox-circle-line text-base" />
              {tk("questionsConfirm")}
            </>
          ) : (
            tk("questionsBtn")
          )}
        </button>
      </div>

      {/* ── Signature pad + date (same card as reference) ── */}
      <div className="bg-white rounded-2xl border border-slate-100 px-4 py-4 mb-4" data-field="signature">
        <SignaturePad
          hasSigned={hasSigned}
          onSign={setHasSigned}
          onSignatureData={(b64) => {
            update("signature", b64);
            if (b64) setBlankSigHint(false);
          }}
          onRejectedBlankSave={handleRejectedBlankSave}
          onClearBlankWarning={handleClearBlankHint}
          initialSignature={data.signature || undefined}
          canvasRef={canvasRef}
          signatureLabel={tk("signatureLabel")}
          clearLabel={tk("clearSignature")}
          hintText={tk("signatureHint")}
          bottomLabel={tk("signatureBottom")}
        />
        {blankSigHint && (
          <p className="text-[12px] text-rose-600 mt-2" role="alert">
            {tk("signatureBlank")}
          </p>
        )}
      </div>

      <div className="flex items-center gap-2 mb-2">
        <i className="ri-calendar-line text-slate-500 text-sm shrink-0" />
        <p className="text-[13px] text-slate-500">
          {tk("dateLabel")}{" "}
          <span className="font-semibold text-slate-700">{today}</span>
          <span className="text-slate-500 ml-2 text-[12px]">{tk("dateAuto")}</span>
        </p>
      </div>

      {/* ── Validation hint ── */}
      {!formComplete && (
        <div className="flex items-center gap-2 mt-3 px-3 py-2.5 bg-amber-50 border border-amber-100 rounded-xl">
          <i className="ri-error-warning-line text-amber-500 text-sm shrink-0" />
          <p className="text-[12px] text-amber-700">
            {tk("validationHint")}
          </p>
        </div>
      )}

    </div>
  );
}
